import { useState, useEffect, useRef } from "react";
import { Plus, Trash2, ExternalLink, Building2, Handshake, Heart, Camera, Image, X, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get full image URL - handles all URL formats
const getImageUrl = (url) => {
  if (!url) return '';
  
  // If it's a relative URL, prepend the backend URL
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  
  // If it's an old preview URL, replace with current backend URL
  if (url.includes('.preview.emergentagent.com') || url.includes('.emergent.host')) {
    const path = url.replace(/https?:\/\/[^\/]+/, '');
    return `${BACKEND_URL}${path}`;
  }
  
  // Otherwise return as is (external URLs)
  return url;
};

const partnerTypes = [
  { value: "sponsor", label: "Sponsor", icon: Heart, color: "bg-amber-500" },
  { value: "community", label: "Community Partner", icon: Handshake, color: "bg-blue-500" },
  { value: "corporate", label: "Corporate Partner", icon: Building2, color: "bg-emerald-500" }
];

export default function Partners() {
  const { isAdmin } = useAuth();
  const [partners, setPartners] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addPhotoOpen, setAddPhotoOpen] = useState(false);
  const [addingPartner, setAddingPartner] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [filterType, setFilterType] = useState("all");
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const fileInputRef = useRef(null);
  const [newPartner, setNewPartner] = useState({
    name: "",
    description: "",
    logo_url: "",
    website_url: "",
    partner_type: "community"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [partnersRes, photosRes] = await Promise.all([
        axios.get(`${API}/partners`),
        axios.get(`${API}/partners/photos`)
      ]);
      setPartners(partnersRes.data);
      setPhotos(photosRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const validFiles = [];
    const previews = [];
    
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 10MB.`);
        continue;
      }
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image file.`);
        continue;
      }
      validFiles.push(file);
      previews.push(URL.createObjectURL(file));
    }
    
    if (validFiles.length > 0) {
      setSelectedFiles(validFiles);
      setPreviewUrls(previews);
      toast.success(`${validFiles.length} photo(s) selected! Tap 'Upload Photos' to add them.`);
    }
  };

  const removeSelectedFile = (index) => {
    const newFiles = [...selectedFiles];
    const newPreviews = [...previewUrls];
    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);
    setSelectedFiles(newFiles);
    setPreviewUrls(newPreviews);
  };

  const handleUploadPhotos = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      toast.error("Please select photos to upload");
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    const uploadedPhotos = [];
    const totalFiles = selectedFiles.length;
    
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const formData = new FormData();
        formData.append("file", file);
        formData.append("title", file.name.split('.')[0]);
        formData.append("description", "");
        
        const uploadRes = await axios.post(`${API}/upload/image`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000
        });
        
        const photoData = {
          title: file.name.split('.')[0],
          description: "",
          image_url: uploadRes.data.url
        };
        
        const response = await axios.post(`${API}/partners/photos`, photoData);
        uploadedPhotos.push(response.data);
        
        setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
      }
      
      setPhotos([...uploadedPhotos.reverse(), ...photos]);
      resetPhotoForm();
      toast.success(`Successfully uploaded ${uploadedPhotos.length} photo(s)!`);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload photos");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const resetPhotoForm = () => {
    setSelectedFiles([]);
    setPreviewUrls([]);
    setAddPhotoOpen(false);
    setUploadProgress(0);
  };

  const handleAddPartner = async (e) => {
    e.preventDefault();
    if (!newPartner.name) {
      toast.error("Please enter partner name");
      return;
    }
    setAddingPartner(true);
    try {
      const response = await axios.post(`${API}/partners`, newPartner);
      setPartners([response.data, ...partners]);
      setNewPartner({
        name: "",
        description: "",
        logo_url: "",
        website_url: "",
        partner_type: "community"
      });
      setAddDialogOpen(false);
      toast.success("Partner added successfully!");
    } catch (error) {
      toast.error("Failed to add partner");
    } finally {
      setAddingPartner(false);
    }
  };

  const handleDeletePartner = async (partnerId) => {
    if (!window.confirm("Remove this partner?")) return;
    try {
      await axios.delete(`${API}/partners/${partnerId}`);
      setPartners(partners.filter(p => p.id !== partnerId));
      toast.success("Partner removed");
    } catch (error) {
      toast.error("Failed to remove partner");
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(`${API}/partners/photos/${photoId}`);
      setPhotos(photos.filter(p => p.id !== photoId));
      toast.success("Photo deleted");
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  const handleTogglePhotoFeatured = async (photoId) => {
    try {
      const response = await axios.patch(`${API}/partners/photos/${photoId}/feature`);
      setPhotos(photos.map(p => 
        p.id === photoId ? { ...p, is_featured: response.data.is_featured } : p
      ));
      toast.success(response.data.is_featured ? "Photo featured!" : "Photo unfeatured");
    } catch (error) {
      toast.error("Failed to update featured status");
    }
  };

  // Sort photos with featured first
  const sortedPhotos = [...photos].sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
  const featuredPhotosCount = photos.filter(p => p.is_featured).length;

  const filteredPartners = filterType === "all" 
    ? partners 
    : partners.filter(p => p.partner_type === filterType);

  const getPartnerTypeInfo = (type) => {
    return partnerTypes.find(t => t.value === type) || partnerTypes[1];
  };

  const sponsorCount = partners.filter(p => p.partner_type === "sponsor").length;
  const communityCount = partners.filter(p => p.partner_type === "community").length;
  const corporateCount = partners.filter(p => p.partner_type === "corporate").length;

  return (
    <div data-testid="partners-page">
      {/* Hero Section */}
      <section className="gradient-hero py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block bg-[#0056D2]/10 text-[#0056D2] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Our Network
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold font-['Outfit'] text-[#0F172A] mb-6">
              Community Partners & Sponsors
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              We are grateful for the support of our partners and sponsors who help us build peace in our communities. Together, we are making a difference.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Heart className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0F172A]">{sponsorCount}</p>
                <p className="text-sm text-slate-500">Sponsors</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Handshake className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0F172A]">{communityCount}</p>
                <p className="text-sm text-slate-500">Community Partners</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0F172A]">{corporateCount}</p>
                <p className="text-sm text-slate-500">Corporate Partners</p>
              </div>
            </div>
            {featuredPhotosCount > 0 && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Star className="w-5 h-5 text-amber-600" fill="currentColor" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0F172A]">{featuredPhotosCount}</p>
                  <p className="text-sm text-slate-500">Featured</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-[#0F172A]">
                Partner Event Photos
              </h2>
              <p className="text-slate-600 mt-2">Photos from our community partnership events</p>
            </div>
            
            {/* Add Photo Dialog */}
            <Dialog open={addPhotoOpen} onOpenChange={(open) => { if (!open) resetPhotoForm(); else setAddPhotoOpen(true); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#0056D2] hover:bg-[#0046B2] text-white rounded-full" data-testid="add-partner-photo-btn">
                  <Camera className="w-4 h-4 mr-2" />
                  Add Photos
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-['Outfit']">Upload Partner Photos</DialogTitle>
                </DialogHeader>
                
                <form onSubmit={handleUploadPhotos} className="space-y-4 mt-4">
                  <div>
                    <Label className="text-slate-700">Select Photos</Label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      id="partner-photo-upload"
                    />
                    <label 
                      htmlFor="partner-photo-upload"
                      className="mt-2 flex items-center justify-center gap-2 bg-[#0056D2] hover:bg-[#0046B2] text-white py-4 px-6 rounded-xl cursor-pointer font-semibold transition-colors active:bg-[#003d99] touch-manipulation"
                    >
                      <Camera className="w-5 h-5" />
                      {selectedFiles.length > 0 ? `${selectedFiles.length} Photo(s) Selected - Tap to Add More` : "Choose Photos from Phone"}
                    </label>
                    
                    {previewUrls.length > 0 && (
                      <div className="mt-4 p-3 bg-slate-100 rounded-xl">
                        <div className="grid grid-cols-4 gap-1 mb-2 max-h-24 overflow-hidden">
                          {previewUrls.slice(0, 8).map((url, index) => (
                            <div key={`preview-${index}-${url.substring(0, 20)}`} className="relative">
                              <img src={url} alt={`Preview ${index + 1}`} className="w-full h-12 object-cover rounded" />
                              <button
                                type="button"
                                onClick={() => removeSelectedFile(index)}
                                className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5"
                              >
                                <X className="w-2 h-2 text-white" />
                              </button>
                            </div>
                          ))}
                        </div>
                        {previewUrls.length > 8 && (
                          <p className="text-xs text-slate-500 text-center">+{previewUrls.length - 8} more photos</p>
                        )}
                        <p className="text-xs text-green-600 text-center">✓ {selectedFiles.length} photo(s) ready</p>
                        
                        {uploading && uploadProgress > 0 && (
                          <div className="mt-2">
                            <div className="w-full bg-slate-300 rounded-full h-2">
                              <div className="bg-[#0056D2] h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                            </div>
                            <p className="text-xs text-center text-slate-500 mt-1">Uploading... {uploadProgress}%</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {previewUrls.length === 0 && (
                      <p className="text-xs text-slate-500 text-center mt-2">Select multiple photos • Max 10MB each</p>
                    )}
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-green-600 hover:bg-green-700 py-4 text-lg font-bold" 
                    disabled={uploading || selectedFiles.length === 0}
                  >
                    {uploading ? `Uploading... ${uploadProgress}%` : `📤 Upload ${selectedFiles.length || ''} Photo${selectedFiles.length !== 1 ? 's' : ''}`}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Photos Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-10 h-10 border-4 border-[#0056D2] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {sortedPhotos.map((photo) => (
                <div 
                  key={photo.id} 
                  className={`relative aspect-square bg-white rounded-xl overflow-hidden group cursor-pointer shadow-sm hover:shadow-md transition-shadow ${photo.is_featured ? 'ring-2 ring-amber-400' : ''}`}
                  onClick={() => setSelectedPhoto(photo)}
                  data-testid={`partner-photo-${photo.id}`}
                >
                  {/* Featured Badge */}
                  {photo.is_featured && (
                    <div className="absolute top-2 left-2 z-10 bg-amber-400 text-amber-900 px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                      <Star className="w-3 h-3" fill="currentColor" />
                      Featured
                    </div>
                  )}
                  <img 
                    src={getImageUrl(photo.image_url)} 
                    alt={photo.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <h3 className="text-white font-medium text-sm line-clamp-2">{photo.title}</h3>
                    </div>
                  </div>
                  {/* Action buttons */}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleTogglePhotoFeatured(photo.id); }}
                      className={`p-1.5 rounded-full transition-colors ${photo.is_featured ? 'bg-amber-400 text-amber-900' : 'bg-black/50 text-white hover:bg-amber-400 hover:text-amber-900'}`}
                      title={photo.is_featured ? "Remove from featured" : "Add to featured"}
                    >
                      <Star className="w-4 h-4" fill={photo.is_featured ? "currentColor" : "none"} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo.id); }}
                      className="p-1.5 bg-black/50 rounded-full text-white hover:bg-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
              <Image className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#0F172A] mb-2">No Photos Yet</h3>
              <p className="text-slate-500 mb-6">Add photos from partner events and community activities.</p>
              <Button onClick={() => setAddPhotoOpen(true)} className="bg-[#0056D2] hover:bg-[#0046B2]">
                <Plus className="w-5 h-5 mr-2" />
                Add First Photo
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Partners List Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
            <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-[#0F172A]">
              Our Partners
            </h2>
            <div className="flex flex-wrap gap-3 items-center">
              {/* Filter Buttons */}
              <div className="flex gap-2">
                <Button 
                  variant={filterType === "all" ? "default" : "outline"}
                  className={`rounded-full ${filterType === "all" ? "bg-[#0056D2]" : ""}`}
                  onClick={() => setFilterType("all")}
                >
                  All ({partners.length})
                </Button>
                {partnerTypes.map(type => (
                  <Button 
                    key={type.value}
                    variant={filterType === type.value ? "default" : "outline"}
                    className={`rounded-full ${filterType === type.value ? type.color : ""}`}
                    onClick={() => setFilterType(type.value)}
                  >
                    {type.label}
                  </Button>
                ))}
              </div>
              
              {/* Add Partner Dialog */}
              <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#0056D2] hover:bg-[#0046B2] text-white rounded-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Partner
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white border-slate-200 max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-['Outfit']">Add New Partner</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddPartner} className="space-y-4 mt-4">
                    <div>
                      <Label>Partner Name *</Label>
                      <Input
                        value={newPartner.name}
                        onChange={(e) => setNewPartner({ ...newPartner, name: e.target.value })}
                        placeholder="Organization name"
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label>Partner Type</Label>
                      <Select 
                        value={newPartner.partner_type} 
                        onValueChange={(value) => setNewPartner({ ...newPartner, partner_type: value })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {partnerTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={newPartner.description}
                        onChange={(e) => setNewPartner({ ...newPartner, description: e.target.value })}
                        placeholder="Brief description of the partner"
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Logo URL</Label>
                      <Input
                        value={newPartner.logo_url}
                        onChange={(e) => setNewPartner({ ...newPartner, logo_url: e.target.value })}
                        placeholder="https://..."
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Website URL</Label>
                      <Input
                        value={newPartner.website_url}
                        onChange={(e) => setNewPartner({ ...newPartner, website_url: e.target.value })}
                        placeholder="https://..."
                        className="mt-1"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#0056D2] hover:bg-[#0046B2]" disabled={addingPartner}>
                      {addingPartner ? "Adding..." : "Add Partner"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Partners Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-10 h-10 border-4 border-[#0056D2] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredPartners.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPartners.map((partner) => {
                const typeInfo = getPartnerTypeInfo(partner.partner_type);
                const TypeIcon = typeInfo.icon;
                return (
                  <div 
                    key={partner.id}
                    className="bg-white rounded-2xl border border-slate-100 p-6 hover:shadow-lg transition-shadow group"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-10 h-10 ${typeInfo.color} rounded-lg flex items-center justify-center`}>
                        <TypeIcon className="w-5 h-5 text-white" />
                      </div>
                      <button
                        onClick={() => handleDeletePartner(partner.id)}
                        className="p-1.5 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {partner.logo_url && (
                      <img 
                        src={partner.logo_url} 
                        alt={partner.name}
                        className="h-16 object-contain mb-4"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    
                    <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{partner.name}</h3>
                    <span className="inline-block text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-full mb-3">
                      {typeInfo.label}
                    </span>
                    
                    {partner.description && (
                      <p className="text-sm text-slate-600 line-clamp-3 mb-4">{partner.description}</p>
                    )}
                    
                    {partner.website_url && (
                      <a 
                        href={partner.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-[#0056D2] hover:underline"
                      >
                        Visit Website
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 bg-slate-50 rounded-2xl">
              <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#0F172A] mb-2">
                {filterType === "all" ? "No Partners Yet" : `No ${getPartnerTypeInfo(filterType).label}s Yet`}
              </h3>
              <p className="text-slate-500 mb-6">Add your first partner to get started.</p>
              <Button onClick={() => setAddDialogOpen(true)} className="bg-[#0056D2] hover:bg-[#0046B2]">
                <Plus className="w-5 h-5 mr-2" />
                Add Partner
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#0056D2] to-[#0044A5] rounded-3xl p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-6">
              Become a Partner
            </h2>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto mb-8">
              Join our network of organizations committed to building peace in Milwaukee. Together, we can create lasting change in our communities.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/contact">
                <Button className="bg-white text-[#0056D2] hover:bg-blue-50 rounded-full px-8 py-3 font-semibold">
                  Contact Us
                </Button>
              </a>
              <a href="/donate">
                <Button className="bg-[#F59E0B] text-white hover:bg-[#D97706] rounded-full px-8 py-3 font-semibold">
                  <Heart className="w-5 h-5 mr-2" />
                  Become a Sponsor
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Lightbox */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-4xl p-0 bg-black border-none overflow-hidden">
          <DialogTitle className="sr-only">{selectedPhoto?.title}</DialogTitle>
          {selectedPhoto && (
            <div>
              <img 
                src={getImageUrl(selectedPhoto.image_url)} 
                alt={selectedPhoto.title}
                className="w-full max-h-[80vh] object-contain"
              />
              <div className="p-4 bg-black/80">
                <h3 className="text-lg font-semibold text-white">{selectedPhoto.title}</h3>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
